import { createAsyncThunk } from "@reduxjs/toolkit";
import { getAllArableFieldGeoms, getAllFieldGeoms, RootState } from "model";
import { initializeFarm } from "model/appSlice";
import { fetchFarmById } from "model/farmSlice";
import { setZoomToGeom } from "model/mapSlice";

export type SBIImportPayload = {
  SBINumber: number;
  userGuid: string;
  farmGuid: string;
};

type SBIImportResponse = {
  farmGuid: string;
  fieldGuids: string[];
  fieldsCreated: number;
};

class SBIImportAPI {
  private baseUrl: string;
  private entity: string;

  constructor(entity: string) {
    this.baseUrl = `${process.env.REACT_APP_API_ENDPOINT}/v1/`;
    this.entity = entity;
  }

  async importSBI(payload: SBIImportPayload): Promise<SBIImportResponse> {
    const res = await fetch(`${this.baseUrl}${this.entity}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return res.json() as Promise<SBIImportResponse>;
  }
}

const sbiImportApi = new SBIImportAPI("SBIImport");

// Function for importing SBI fields to existing farm
export const importSBIToExistingFarm = createAsyncThunk(
  "sbiImport/importToExistingFarm",
  async (
    { sbiNumber, farmGuid }: { sbiNumber: number; farmGuid: string },
    { getState, dispatch }
  ) => {
    const state = getState() as { user: { details?: { guid: string } } };
    const userGuid = state.user.details?.guid;

    if (!userGuid) {
      throw new Error("User not authenticated");
    }

    const response = await sbiImportApi.importSBI({
      SBINumber: sbiNumber,
      userGuid,
      farmGuid,
    });

    // After import, refresh the farm data to get correct field categorization
    if (response.fieldGuids && response.fieldGuids.length > 0) {
      const state = getState() as RootState;
      const currentFarmId = state.farms.currentFarmId;

      if (currentFarmId) {
        // Fetch the updated farm data (includes new field IDs)
        const updatedFarm = await dispatch(
          fetchFarmById(currentFarmId)
        ).unwrap();

        // Initialize the farm with the updated data
        await dispatch(initializeFarm(updatedFarm));
      }

      // Center the map on the newly imported fields
      setTimeout(() => {
        const state = getState() as RootState;
        const regularFieldGeoms = getAllFieldGeoms(state);
        const arableFieldGeoms = getAllArableFieldGeoms(state);

        if (regularFieldGeoms.features.length > 0) {
          dispatch(setZoomToGeom(regularFieldGeoms));
        } else if (arableFieldGeoms.features.length > 0) {
          dispatch(setZoomToGeom(arableFieldGeoms));
        }
      }, 500);
    }
    return response;
  }
);
